import { useState } from 'react';
import { ClientProfileContentItem } from 'store/clientProfile/clientProfileSlice';
import { Status as ClientStatus } from 'store/clients/clientsSlice';
import { useAppSelector } from 'store/hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { NoteForm } from '../NoteForm';
import { PopupDraftNoteFormProvider, useNoteFormContext } from '../context';
import { AiScribeMedia, PopupDraftActions } from './NoteFormSections';

type PopupNoteFormProps = {
  status: ClientStatus | ClientStatus['id'];
  clientId: string;
  note?: ClientProfileContentItem;
  disabled?: boolean;
};

const PopupNoteFormContent = ({ onEdit }: { onEdit?: () => void }) => {
  const { enableDateTimeUpdates } = useFlags();
  const { isManagerReview, disabled } = useNoteFormContext();
  return (
    <NoteForm.Form>
      <NoteForm.FieldsContainer expanded={true}>
        <NoteForm.FieldsContainer.Row>
          <NoteForm.FieldsContainer.HalfRow>
            <NoteForm.Fields.NoteType disabled={disabled} />
          </NoteForm.FieldsContainer.HalfRow>
          <NoteForm.FieldsContainer.HalfRow>
            <NoteForm.Fields.DateOfEncounter disabled={disabled} />
          </NoteForm.FieldsContainer.HalfRow>
        </NoteForm.FieldsContainer.Row>

        {enableDateTimeUpdates ? (
          <NoteForm.FieldsContainer.Row>
            <NoteForm.FieldsContainer.HalfRow>
              <NoteForm.Fields.StartTime disabled={disabled} />
            </NoteForm.FieldsContainer.HalfRow>
            <NoteForm.FieldsContainer.HalfRow>
              <NoteForm.Fields.TimeSpent disabled={disabled} />
            </NoteForm.FieldsContainer.HalfRow>
          </NoteForm.FieldsContainer.Row>
        ) : (
          !isManagerReview && (
            <NoteForm.FieldsContainer.Row>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.StartTime disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.TimeSpent disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
            </NoteForm.FieldsContainer.Row>
          )
        )}

        {isManagerReview && (
          <>
            {enableDateTimeUpdates ? (
              <>
                <NoteForm.FieldsContainer.Row>
                  <NoteForm.FieldsContainer.HalfRow>
                    <NoteForm.Fields.Manager disabled={disabled} />
                  </NoteForm.FieldsContainer.HalfRow>
                  <NoteForm.FieldsContainer.HalfRow>
                    <NoteForm.Fields.Chw disabled={disabled} />
                  </NoteForm.FieldsContainer.HalfRow>
                </NoteForm.FieldsContainer.Row>
                <NoteForm.FieldsContainer.Row>
                  <NoteForm.FieldsContainer.FullWidth>
                    <NoteForm.Fields.Provider disabled={disabled} />
                  </NoteForm.FieldsContainer.FullWidth>
                </NoteForm.FieldsContainer.Row>
              </>
            ) : (
              <>
                <NoteForm.FieldsContainer.Row>
                  <NoteForm.FieldsContainer.HalfRow>
                    <NoteForm.Fields.TimeSpent disabled={disabled} />
                  </NoteForm.FieldsContainer.HalfRow>
                  <NoteForm.FieldsContainer.HalfRow>
                    <NoteForm.Fields.Manager disabled={disabled} />
                  </NoteForm.FieldsContainer.HalfRow>
                </NoteForm.FieldsContainer.Row>
                <NoteForm.FieldsContainer.Row>
                  <NoteForm.FieldsContainer.HalfRow>
                    <NoteForm.Fields.Chw disabled={disabled} />
                  </NoteForm.FieldsContainer.HalfRow>
                  <NoteForm.FieldsContainer.HalfRow>
                    <NoteForm.Fields.Provider disabled={disabled} />
                  </NoteForm.FieldsContainer.HalfRow>
                </NoteForm.FieldsContainer.Row>
              </>
            )}
          </>
        )}

        {!isManagerReview && (
          <>
            <NoteForm.FieldsContainer.Row>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.NextContact disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
            </NoteForm.FieldsContainer.Row>
            <NoteForm.FieldsContainer.Row>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.WhoDidYouReach disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
            </NoteForm.FieldsContainer.Row>
            <NoteForm.FieldsContainer.FullWidth>
              <NoteForm.Fields.TypeOfSupport disabled={disabled} />
            </NoteForm.FieldsContainer.FullWidth>
          </>
        )}

        <AiScribeMedia />
        <NoteForm.FieldsContainer.HalfRow>
          <NoteForm.Fields.Note disabled={disabled} />
        </NoteForm.FieldsContainer.HalfRow>
        <NoteForm.FieldsContainer.HalfRow>
          <NoteForm.Fields.DocumentationTime disabled={disabled} />
        </NoteForm.FieldsContainer.HalfRow>
      </NoteForm.FieldsContainer>
      <PopupDraftActions onEdit={onEdit} />
    </NoteForm.Form>
  );
};

/**
 * Preset component for editing notes in a popup.
 * Disabled state is controlled from outside via props.
 * Includes AI Scribe status, transcript, and audio player.
 * Autosave is enabled only for draft notes.
 */
export const PopupNoteForm = ({ status, clientId, note, disabled = true }: PopupNoteFormProps) => {
  const { data } = useAppSelector((state) => state.clients.client);
  const [isEditing, setIsEditing] = useState(!disabled);
  const onEdit = () => {
    setIsEditing(true);
  };

  return (
    <PopupDraftNoteFormProvider
      status={status}
      clientId={clientId}
      note={note}
      disabled={!isEditing}
      clientName={data?.firstName + ' ' + data?.lastName ?? ''}
      prependedClientId={data?.prependedId ?? ''}
    >
      <NoteForm.Root>
        <PopupNoteFormContent onEdit={onEdit} />
      </NoteForm.Root>
    </PopupDraftNoteFormProvider>
  );
};
